'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import HomepageNav from '@/components/HomepageNav';
import HomepageFooter from '@/app/components/HomepageFooter';
import {
  ChevronRight, Trophy, TrendingUp, Award, Users, Zap,
  Crown, Medal, Star, RefreshCw
} from 'lucide-react';

const ACCENT = '#D4AF37';
const RANK_COLORS = ['#f59e0b', '#9ca3af', '#cd7f32'];

const BOARDS = [
  { key: 'profit',    label: 'Top Profit %',   icon: TrendingUp },
  { key: 'payout',   label: 'Highest Payout',  icon: Trophy     },
  { key: 'streak',   label: 'Win Streak',       icon: Zap        },
  { key: 'affiliate',label: 'Top Affiliates',   icon: Users      },
];

interface Trader {
  rank: number;
  user_id: number;
  full_name: string;
  country_flag: string;
  profit_percentage: number;
  total_payout: number;
  win_streak: number;
  affiliate_count: number;
  total_trades: number;
}

interface GlobalStats {
  total_traders: number;
  total_payouts: number;
  top_profit: number;
  top_streak: number;
}

function getInitials(name: string) {
  if (!name) return '??';
  const parts = name.trim().split(' ').filter(Boolean);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function Avatar({ name, size = 40, bg, color }: { name: string; size?: number; bg?: string; color?: string }) {
  return (
    <div
      className="rounded-full flex items-center justify-center font-extrabold flex-shrink-0"
      style={{ width: size, height: size, background: bg || '#000000', color: color || '#888', fontSize: size * 0.35 }}
    >
      {name ? getInitials(name) : '??'}
    </div>
  );
}

export default function LeaderboardPage() {
  const [board, setBoard] = useState('profit');
  const [traders, setTraders] = useState<Trader[]>([]);
  const [globalStats, setGlobalStats] = useState<GlobalStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  async function fetchData(isRefresh = false) {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      const res = await fetch(`/api/leaderboard?category=${board}&limit=50`);
      const data = await res.json();
      setTraders(data.traders || []);
      setGlobalStats(data.globalStats || null);
    } catch { /* silent */ } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  useEffect(() => { fetchData(); }, [board]);

  function getValue(t: Trader) {
    if (board === 'profit') return `${Number(t.profit_percentage).toFixed(1)}%`;
    if (board === 'payout') return `$${Number(t.total_payout).toLocaleString()}`;
    if (board === 'streak') return `${t.win_streak} days`;
    if (board === 'affiliate') return `${t.affiliate_count} refs`;
    return '—';
  }

  function getRaw(t: Trader) {
    if (board === 'profit') return Number(t.profit_percentage);
    if (board === 'payout') return Number(t.total_payout);
    if (board === 'streak') return Number(t.win_streak);
    if (board === 'affiliate') return Number(t.affiliate_count);
    return 0;
  }

  const maxVal = traders.length > 0 ? (getRaw(traders[0]) || 1) : 1;
  const top3 = [traders[0] || null, traders[1] || null, traders[2] || null];
  const podiumOrder = [top3[1], top3[0], top3[2]];
  const podiumPos = [2, 1, 3] as const;

  const TIER_BADGES: Record<string, { bg: string; color: string }> = {
    Elite:  { bg: 'rgba(212,175,55,0.15)',  color: '#D4AF37' },
    Pro:    { bg: 'rgba(245,158,11,0.15)', color: '#f59e0b' },
    Rising: { bg: 'rgba(34,197,94,0.15)',  color: '#22c55e' },
  };

  function getTier(rank: number) {
    if (rank <= 3) return 'Elite';
    if (rank <= 7) return 'Pro';
    return 'Rising';
  }

  return (
    <div className="min-h-screen" style={{ background: '#000000' }}>
      <HomepageNav />

      <main className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-10 py-12">

        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-xs mb-10" style={{ color: '#444' }}>
          <Link href="/" className="hover:text-white transition-colors">Home</Link>
          <ChevronRight size={12} />
          <span className="text-white">Leaderboard</span>
        </div>

        {/* Hero */}
        <div className="text-center mb-12 space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase"
            style={{ background: '#000000', border: '1px solid #222', color: '#555' }}>
            <Star size={11} color={ACCENT} />
            Live Rankings
          </div>
          <h1 className="text-4xl xl:text-5xl font-extrabold text-white">
            Trader <span style={{ color: ACCENT }}>Leaderboard</span>
          </h1>
          <p className="text-base max-w-xl mx-auto leading-relaxed" style={{ color: '#666' }}>
            The top funded traders on HiyaPrime, ranked live from our database. Updated daily.
          </p>
        </div>

        {/* Global stat strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10">
          {[
            { label: 'Ranked Traders', value: globalStats ? Number(globalStats.total_traders).toLocaleString() : '—', icon: Users, color: '#6366f1' },
            { label: 'Total Paid Out',  value: globalStats ? `$${Number(globalStats.total_payouts).toLocaleString()}` : '—', icon: Trophy, color: '#f59e0b' },
            { label: 'Top Profit',      value: globalStats ? `${Number(globalStats.top_profit).toFixed(1)}%` : '—', icon: TrendingUp, color: '#22c55e' },
            { label: 'Longest Streak',  value: globalStats ? `${globalStats.top_streak}d` : '—', icon: Zap, color: ACCENT },
          ].map(s => (
            <div key={s.label} className="rounded-2xl p-4 flex items-center gap-3"
              style={{ background: '#000000', border: '1px solid #000000' }}>
              <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: s.color + '18' }}>
                <s.icon size={16} color={s.color} />
              </div>
              <div>
                <div className="text-xs" style={{ color: '#555' }}>{s.label}</div>
                <div className="text-lg font-extrabold text-white">{s.value}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Category tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {BOARDS.map(b => {
            const Icon = b.icon;
            const active = board === b.key;
            return (
              <button
                key={b.key}
                onClick={() => setBoard(b.key)}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all"
                style={{
                  background: active ? ACCENT : '#000000',
                  color: active ? '#fff' : '#555',
                  border: `1px solid ${active ? ACCENT : '#222'}`,
                }}
              >
                <Icon size={14} />
                {b.label}
              </button>
            );
          })}
          <button
            onClick={() => fetchData(true)}
            className="ml-auto flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-colors"
            style={{ background: '#000000', border: '1px solid #222', color: '#555' }}
          >
            <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>

        {/* Podium always shown */}
        <div className="grid grid-cols-3 gap-3 mb-8 items-end">
          {podiumOrder.map((t, i) => {
            const pos = podiumPos[i];
            const color = RANK_COLORS[pos - 1];
            const heights = [48, 72, 32];

            function podiumVal() {
              if (!t) return '—';
              return getValue(t);
            }

            return (
              <div key={pos} className="flex flex-col items-center">
                <div
                  className="w-full rounded-2xl p-4 text-center flex flex-col items-center gap-2 relative"
                  style={{
                    background: t ? color + '0d' : '#000000',
                    border: `1px solid ${t ? color + '44' : '#000000'}`,
                    marginBottom: 0,
                  }}
                >
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    {pos === 1 && <Crown size={20} color="#f59e0b" />}
                    {pos === 2 && <Medal size={18} color="#9ca3af" />}
                    {pos === 3 && <Medal size={18} color="#cd7f32" />}
                  </div>

                  <Avatar
                    name={t?.full_name || ''}
                    size={pos === 1 ? 56 : 44}
                    bg={t ? color + '33' : '#161616'}
                    color={t ? color : '#2a2a2a'}
                  />
                  <div>
                    <div className="font-bold text-white text-sm leading-tight">
                      {t ? t.full_name : <span style={{ color: '#2a2a2a' }}>—</span>}
                    </div>
                    <div className="text-xs mt-0.5" style={{ color: '#555' }}>
                      {t ? t.country_flag : ['2nd', '1st', '3rd'][i]}
                    </div>
                  </div>
                  <div className="text-xl font-extrabold font-mono" style={{ color: t ? color : '#000000' }}>
                    {podiumVal()}
                  </div>
                </div>
                <div
                  className="w-full rounded-b-xl flex items-center justify-center text-sm font-extrabold"
                  style={{ height: heights[i], background: color + '1a', color }}
                >
                  #{pos}
                </div>
              </div>
            );
          })}
        </div>

        {/* Full table */}
        <div className="rounded-2xl overflow-hidden mb-10" style={{ border: '1px solid #000000' }}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: '1px solid #000000', background: '#000000' }}>
                  <th className="text-left px-5 py-3 text-xs font-semibold" style={{ color: '#444' }}>Rank</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold" style={{ color: '#444' }}>Trader</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold" style={{ color: '#444' }}>
                    {BOARDS.find(b => b.key === board)?.label}
                  </th>
                  <th className="text-right px-5 py-3 text-xs font-semibold hidden md:table-cell" style={{ color: '#444' }}>Profit %</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold hidden md:table-cell" style={{ color: '#444' }}>Total Payout</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold hidden lg:table-cell" style={{ color: '#444' }}>Win Streak</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold hidden lg:table-cell" style={{ color: '#444' }}>Trades</th>
                  <th className="text-center px-5 py-3 text-xs font-semibold" style={{ color: '#444' }}>Tier</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 10 }).map((_, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #000000' }}>
                      <td colSpan={8} className="px-5 py-3">
                        <div className="h-8 rounded-lg animate-pulse" style={{ background: '#161616' }} />
                      </td>
                    </tr>
                  ))
                ) : traders.length === 0 ? (
                  <tr>
                    <td colSpan={8}>
                      <div className="py-16 flex flex-col items-center gap-3">
                        <Trophy size={40} color="#000000" />
                        <div className="text-base font-bold" style={{ color: '#333' }}>No traders ranked yet</div>
                        <div className="text-sm" style={{ color: '#2a2a2a' }}>Be the first to complete a funded challenge</div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  traders.map((t, i) => {
                    const tier = getTier(t.rank);
                    const tc = TIER_BADGES[tier];
                    const rankColor = t.rank <= 3 ? RANK_COLORS[t.rank - 1] : '#3f3f3f';
                    const pct = maxVal > 0 ? (getRaw(t) / maxVal) * 100 : 0;
                    return (
                      <tr
                        key={t.user_id}
                        className="transition-colors"
                        style={{
                          borderBottom: '1px solid #000000',
                          background: i % 2 === 0 ? '#000000' : 'transparent',
                        }}
                        onMouseEnter={e => (e.currentTarget.style.background = '#161616')}
                        onMouseLeave={e => (e.currentTarget.style.background = i % 2 === 0 ? '#000000' : 'transparent')}
                      >
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-1.5">
                            {t.rank === 1 && <Crown size={13} color="#f59e0b" />}
                            {t.rank === 2 && <Medal size={13} color="#9ca3af" />}
                            {t.rank === 3 && <Medal size={13} color="#cd7f32" />}
                            <span className="font-bold font-mono text-sm" style={{ color: rankColor }}>#{t.rank}</span>
                          </div>
                        </td>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2.5">
                            <Avatar
                              name={t.full_name}
                              size={32}
                              bg={rankColor + '22'}
                              color={rankColor}
                            />
                            <div>
                              <div className="font-semibold text-white text-sm">{t.full_name}</div>
                              <div className="text-xs" style={{ color: '#555' }}>{t.country_flag}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <div className="w-16 h-1 rounded-full" style={{ background: '#000000' }}>
                              <div className="h-full rounded-full" style={{ width: `${pct}%`, background: ACCENT }} />
                            </div>
                            <span className="font-bold font-mono text-sm" style={{ color: '#22c55e' }}>{getValue(t)}</span>
                          </div>
                        </td>
                        <td className="px-5 py-3 text-right font-mono text-sm hidden md:table-cell" style={{ color: '#888' }}>
                          {Number(t.profit_percentage).toFixed(1)}%
                        </td>
                        <td className="px-5 py-3 text-right font-mono text-sm hidden md:table-cell" style={{ color: '#888' }}>
                          ${Number(t.total_payout).toLocaleString()}
                        </td>
                        <td className="px-5 py-3 text-right font-mono text-sm hidden lg:table-cell" style={{ color: '#888' }}>
                          {t.win_streak}d
                        </td>
                        <td className="px-5 py-3 text-right font-mono text-sm hidden lg:table-cell" style={{ color: '#888' }}>
                          {t.total_trades}
                        </td>
                        <td className="px-5 py-3 text-center">
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                            style={{ background: tc.bg, color: tc.color }}>
                            {tier}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Tier info */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
          {[
            { icon: Crown, title: 'Elite Tier', color: ACCENT, desc: 'Traders who consistently deliver 10%+ monthly profit with controlled drawdown. Top 5% of all funded accounts.' },
            { icon: TrendingUp, title: 'Pro Tier', color: '#f59e0b', desc: 'Established funded traders with a track record of profitable months and strong risk management.' },
            { icon: Award, title: 'Rising Tier', color: '#22c55e', desc: 'New funded traders showing strong early performance, on track to move into Pro or Elite.' },
          ].map(item => (
            <div key={item.title} className="rounded-2xl p-5 space-y-3"
              style={{ background: '#000000', border: '1px solid #000000' }}>
              <div className="w-9 h-9 rounded-xl flex items-center justify-center"
                style={{ background: item.color + '18' }}>
                <item.icon size={16} color={item.color} />
              </div>
              <div className="font-bold text-white">{item.title}</div>
              <p className="text-xs leading-relaxed" style={{ color: '#555' }}>{item.desc}</p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="rounded-2xl p-8 text-center space-y-4"
          style={{ background: 'rgba(212,175,55,0.06)', border: '1px solid rgba(212,175,55,0.18)' }}>
          <div className="text-2xl font-extrabold text-white">Your name could be here</div>
          <p className="text-sm max-w-md mx-auto" style={{ color: '#666' }}>
            Pass the evaluation and join the top traders already earning with HiyaPrime capital.
          </p>
          <Link
            href="/register"
            className="inline-block px-8 py-3 rounded-xl text-sm font-bold text-white"
            style={{ background: ACCENT }}
          >
            Start Your Challenge →
          </Link>
        </div>

      </main>

      <HomepageFooter />
    </div>
  );
}
